import { EDIT_PREVIEW_CLASS_NAME } from '@dreryk/react-components';

export const DICTATION_PREVIEW_CLASS = 'se-ai-dictation-preview';

export const escapeHtml = (text: string): string =>
   text.replace(/&/gu, '&amp;').replace(/</gu, '&lt;').replace(/>/gu, '&gt;');

type PreviewMarker = (escapedText: string) => string;

const noMarker: PreviewMarker = (escapedText) => escapedText;
const previewMarker: PreviewMarker = (escapedText) => `<span class="${EDIT_PREVIEW_CLASS_NAME}">${escapedText}</span>`;

const WHITESPACE = /\s/u;
const WHITESPACE_RUN = /\s+/gu;

const textToParagraphsHtml = (text: string, mark: PreviewMarker): string => {
   const paragraphs = text
      .split(/\n+/u)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => `<p>${mark(escapeHtml(line))}</p>`);
   return paragraphs.length > 0 ? paragraphs.join('') : '<p></p>';
};

type TextPosition = { node: Text; offset: number };
type IndexedText = { text: string; positions: TextPosition[] };

function indexTextNodes(doc: Document): IndexedText {
   const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
   const positions: TextPosition[] = [];
   let text = '';

   for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
      const value = node.nodeValue ?? '';

      for (let offset = 0; offset < value.length; offset += 1) {
         if (!WHITESPACE.test(value[offset])) {
            text += value[offset];
            positions.push({ node: node as Text, offset: offset });
         }
      }
   }

   return { text: text, positions: positions };
}

function findSelectionStart(text: string, needle: string, expectedStart: number | null): number {
   if (needle.length === 0) {
      return -1;
   }
   if (expectedStart !== null && text.startsWith(needle, expectedStart)) {
      return expectedStart;
   }
   return text.indexOf(needle);
}

function removeEmptiedParagraphs(paragraphs: HTMLParagraphElement[]): void {
   paragraphs.filter((paragraph) => paragraph.textContent === '').forEach((paragraph) => paragraph.remove());
}

type SelectionRedactionParams = {
   originalHtml: string;
   selectedText: string;
   selectionStart: number | null;
   redactedText: string;
   mark: PreviewMarker;
};

// eslint-disable-next-line max-statements
function applySelectionRedaction(params: SelectionRedactionParams): string | null {
   const doc = new DOMParser().parseFromString(params.originalHtml, 'text/html');
   const indexed = indexTextNodes(doc);
   const needle = params.selectedText.replace(WHITESPACE_RUN, '');
   const start = findSelectionStart(indexed.text, needle, params.selectionStart);

   if (start < 0) {
      return null;
   }

   const from = indexed.positions[start];
   const to = indexed.positions[start + needle.length - 1];
   const range = doc.createRange();
   range.setStart(from.node, from.offset);
   range.setEnd(to.node, to.offset + 1);

   const touchedParagraphs = Array.from(doc.body.querySelectorAll('p')).filter((paragraph) =>
      range.intersectsNode(paragraph)
   );
   const replacement = doc.createElement('template');
   replacement.innerHTML = params.mark(escapeHtml(params.redactedText));

   range.deleteContents();
   range.insertNode(replacement.content);
   removeEmptiedParagraphs(touchedParagraphs);

   return doc.body.innerHTML;
}

type RedactedHtmlParams = {
   originalHtml: string;
   selectedText: string;
   selectionStart: number | null;
   isSelection: boolean;
   redactedText: string;
   highlighted: boolean;
};

export function buildRedactedHtml(params: RedactedHtmlParams): string {
   const mark = params.highlighted ? previewMarker : noMarker;

   if (!params.isSelection) {
      return textToParagraphsHtml(params.redactedText, mark);
   }

   const redacted = applySelectionRedaction({
      originalHtml: params.originalHtml,
      selectedText: params.selectedText,
      selectionStart: params.selectionStart,
      redactedText: params.redactedText,
      mark: mark,
   });

   return redacted ?? params.originalHtml;
}

export function buildLivePreviewHtml(originalHtml: string, accumulated: string, partial: string): string {
   const combined = (accumulated + partial).trim();
   if (!combined) {
      return originalHtml;
   }
   const escaped = escapeHtml(combined);
   const previewParagraph = `<p><span class="${DICTATION_PREVIEW_CLASS}">${escaped}</span></p>`;
   return originalHtml + previewParagraph;
}
